import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function DraftCheckout() {
  const { kinId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (kinId) {
      fetchDraft();
    }
  }, [kinId]);

  const fetchDraft = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Draft data:', data);
        setDraft(data.draft);
        setGamification(data.gamification);
        setSummary(data.summary);
      } else {
        const err = await res.json();
        setError(err.error || 'Failed to fetch draft');
        console.error('Error fetching draft:', err);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 0) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ quantity: newQuantity })
      });

      if (res.ok) {
        const data = await res.json();
        setDraft(data);
        // Refresh gamification data
        await fetchDraft();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to update quantity');
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert('Error updating quantity');
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm('Remove this item from your draft?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        const data = await res.json();
        setDraft(data);
        await fetchDraft();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to remove item');
      }
    } catch (err) {
      console.error('Error removing item:', err);
      alert('Error removing item');
    }
  };

  const submitDraft = async () => {
    if (!draft || draft.items.length === 0) {
      alert('Your draft is empty');
      return;
    }
    if (!window.confirm('Submit your draft to the coordinator? Once submitted, you cannot make changes.')) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ notes: '' })
      });

      if (res.ok) {
        alert('✅ Draft submitted successfully!');
        navigate(`/kin-dashboard`);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to submit draft');
      }
    } catch (err) {
      console.error('Error submitting draft:', err);
      alert('Error submitting draft');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="text-center py-12">
          <div className="text-gray-600">Loading your draft...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="text-center py-12">
          <div className="text-red-600">Error: {error}</div>
          <button 
            onClick={fetchDraft} 
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!draft || draft.items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="text-center py-12">
          <p className="text-gray-500">Your draft is empty.</p>
          <button 
            onClick={() => navigate(`/member-draft/${kinId}`)} 
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  const isSubmitted = draft.status === 'submitted' || draft.status === 'consolidated';

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">🛒 Your Draft Order</h1>
        <button
          onClick={() => navigate(`/member-draft/${kinId}`)}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          ← Continue Shopping
        </button>
      </div>

      {/* Gamification Banner */}
      {gamification && gamification.hasOpportunities && !isSubmitted && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-orange-300 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🎯</span>
            <div>
              <h3 className="font-bold text-orange-800">Your Kin is close to unlocking bigger discounts!</h3>
              {gamification.biggestOpportunity && (
                <p className="text-sm text-orange-700 mt-1">
                  Add {gamification.biggestOpportunity.neededQuantity} more {gamification.biggestOpportunity.productName} from {gamification.biggestOpportunity.supplierName} to save an additional ${gamification.biggestOpportunity.additionalSavings.toFixed(2)}!
                </p>
              )}
              {gamification.opportunities.length > 1 && (
                <p className="text-xs text-orange-600 mt-1">
                  {gamification.opportunities.length} discount opportunities available
                </p>
              )}
              <div className="mt-2 text-sm">
                <span className="font-medium">💰 Current savings: ${gamification.totalSavings.toFixed(2)}</span>
                <span className="text-gray-500 ml-4">Potential extra: ${gamification.potentialSavings.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200">
          {draft.items.map(item => (
            <div key={item._id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800">{item.productName}</p>
                <p className="text-sm text-gray-500">
                  ${item.unitPrice.toFixed(2)} / {item.unit || 'each'}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                {!isSubmitted && (
                  <>
                    <div className="flex items-center border rounded-lg">
                      <button
                        onClick={() => updateQuantity(item._id, item.quantity - 1)}
                        className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-l-lg"
                        disabled={item.quantity <= 1}
                      >
                        −
                      </button>
                      <span className="px-3 py-1 min-w-[2.5rem] text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item._id, item.quantity + 1)}
                        className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-r-lg"
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => removeItem(item._id)}
                      className="text-red-500 hover:text-red-700 text-sm px-2"
                    >
                      ×
                    </button>
                  </>
                )}
                {isSubmitted && (
                  <span className="text-sm text-gray-500">×{item.quantity}</span>
                )}
                <span className="font-medium text-green-600 min-w-[4rem] text-right">
                  ${item.totalPrice.toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-gray-50 p-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between gap-2">
            <div>
              <span className="text-sm text-gray-500">Total items: {draft.items.reduce((sum, i) => sum + i.quantity, 0)}</span>
              {summary && summary.totalSavings > 0 && (
                <span className="text-sm text-green-600 ml-4">
                  💰 Saved: ${summary.totalSavings.toFixed(2)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-lg font-bold text-green-600">
                ${draft.total.toFixed(2)}
              </span>
              {!isSubmitted && (
                <button
                  onClick={submitDraft}
                  disabled={submitting}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
                >
                  {submitting ? 'Submitting...' : '📤 Submit Draft'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {isSubmitted && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 text-center text-green-700">
          ✅ Draft submitted to coordinator.
        </div>
      )}
    </div>
  );
}
